import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUsers, FaWifi, FaCheckCircle, FaMicrophone,
  FaMapMarkerAlt, FaPhoneAlt, FaBuilding, FaClock,
} from "react-icons/fa";
import { MdOutlineScreenShare, MdAir } from "react-icons/md";

import img1 from "../assets/Conference/1.JPG";
import img2 from "../assets/Conference/2.JPG";
import img3 from "../assets/Conference/3.JPG";
import img4 from "../assets/Conference/4.JPG";
import img5 from "../assets/Conference/5.JPG";
import img6 from "../assets/Conference/6.JPG";
import img7 from "../assets/Conference/7.JPG";

const galleryImages = [
  { src: img1, caption: "Main Conference Hall" },
  { src: img2, caption: "Boardroom Setup" },
  { src: img3, caption: "Theatre Arrangement" },
  { src: img4, caption: "Workshop Space" },
  { src: img5, caption: "Stage & Podium" },
  { src: img6, caption: "Networking Lounge" },
  { src: img7, caption: "Gala Dinner Setup" },
];

const venues = [
  {
    name: "Kanchan",
    subtitle: "Conference / Meeting Room — 1",
    rate: "30,000",
    size: "77 × 20 ft",
    banner: "9×4",
    capacity: { classroom: 70, theatre: 130, ushape: 60, banquet: 60 },
    desc: "Our largest and most prestigious conference hall. Perfect for large-scale seminars, corporate conferences, annual meetings, and training events with multiple seating formats.",
    features: ["Full AV System", "Multiple Seating Formats", "Air Conditioned", "9×4 ft Banner Space", "On-site Catering", "Dedicated Event Coordinator"],
  },
  {
    name: "Nagori",
    subtitle: "Conference / Meeting Room — 2",
    rate: "25,000",
    size: "45 × 19 ft",
    banner: "8×4",
    capacity: { classroom: 32, theatre: 72, ushape: 30, banquet: 36 },
    desc: "A versatile mid-size venue ideal for workshops, departmental meetings, product briefings, and training programmes. Flexible layout to suit any arrangement.",
    features: ["HD Projector & Screen", "High-Speed WiFi", "Air Conditioned", "8×4 ft Banner Space", "Whiteboard & Flip Chart", "Refreshment Station"],
  },
  {
    name: "Rathura",
    subtitle: "Conference / Meeting Room — 3",
    rate: "20,000",
    size: "25 × 19 ft",
    banner: "8×4",
    capacity: { classroom: 15, theatre: 20, ushape: 15, banquet: 15 },
    desc: "An intimate, focused meeting space designed for board-level strategy sessions, confidential discussions, and small-group training in a professional environment.",
    features: ["A/V & Logistic Support", "Video Conferencing Ready", "Air Conditioned", "8×4 ft Banner Space", "Executive Furnishings", "Butler Service on Request"],
  },
];

const facilityRows = [
  {
    sl: 1,
    name: "Conference / Meeting Room — 1",
    venue: "Kanchan",
    rate: "30,000/-++",
    length: 77, width: 20,
    classroom: 70, theatre: 130, ushape: 60, banquet: 60,
    banner: "9×4",
  },
  {
    sl: 2,
    name: "Conference / Meeting Room — 2",
    venue: "Nagori",
    rate: "25,000/-++",
    length: 45, width: 19,
    classroom: 32, theatre: 72, ushape: 30, banquet: 36,
    banner: "8×4",
  },
  {
    sl: 3,
    name: "Conference / Meeting Room — 3",
    venue: "Rathura",
    rate: "20,000/-++",
    length: 25, width: 19,
    classroom: 15, theatre: 20, ushape: 15, banquet: 15,
    banner: "8×4",
  },
];

const amenities = [
  { icon: <FaWifi className="text-xl" />, label: "High-Speed WiFi" },
  { icon: <MdOutlineScreenShare className="text-xl" />, label: "Projectors & Screens" },
  { icon: <MdAir className="text-xl" />, label: "Air Conditioning" },
  { icon: <FaUsers className="text-xl" />, label: "Dedicated Support" },
  { icon: <FaMicrophone className="text-xl" />, label: "A/V & Logistic Support" },
  { icon: <FaBuilding className="text-xl" />, label: "Ample Parking" },
];

/* ── Capacity badge ── */
function CapBadge({ label, value, color }) {
  return (
    <div className={`flex flex-col items-center rounded-xl px-3 py-2 ${color}`}>
      <span className="text-xl font-bold font-fancy leading-none">{value}</span>
      <span className="text-[10px] uppercase tracking-wide mt-0.5 opacity-80">{label}</span>
    </div>
  );
}

/* ── Premium Lightbox ── */
function Lightbox({ images, index, onClose, onPrev, onNext, onJump }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[200] flex flex-col bg-black/95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Top bar */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-6 py-3 bg-black/40 backdrop-blur-sm border-b border-white/10">
          <span className="text-white font-semibold text-base truncate">{images[index].caption}</span>
          <span className="text-white/60 text-sm font-medium mx-4 whitespace-nowrap">{index + 1} / {images.length}</span>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Image area */}
        <div className="flex-1 min-h-0 relative flex items-center justify-center px-14 md:px-20 py-4">
          <button
            onClick={onPrev}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-blue-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
            aria-label="Previous"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              src={images[index].src}
              alt={images[index].caption}
              className="max-h-full max-w-full object-contain rounded-xl shadow-2xl"
              style={{ maxHeight: "calc(100vh - 130px)" }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2 }}
            />
          </AnimatePresence>

          <button
            onClick={onNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-blue-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
            aria-label="Next"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Thumbnail strip */}
        <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-3 bg-black/40 border-t border-white/10" style={{ scrollbarWidth: "none" }}>
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onJump(idx)}
              className={`flex-shrink-0 w-14 h-10 md:w-16 md:h-11 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                idx === index ? "border-blue-400 opacity-100 scale-105" : "border-transparent opacity-50 hover:opacity-80 hover:border-white/40"
              }`}
            >
              <img src={img.src} alt={img.caption} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Main Component ── */
function Conference() {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Hero Banner ── */}
      <div className="relative h-[60vh] min-h-[320px] overflow-hidden">
        <img src={img1} alt="Chuti Convention Centre" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/50 to-slate-900/85" />
        <div className="absolute inset-0 flex flex-col justify-end items-center text-white text-center px-4 pb-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center justify-center gap-3 mb-3">
              <FaBuilding className="text-blue-400 text-xl" />
              <span className="text-blue-300 uppercase tracking-[0.3em] text-xs font-semibold">
                Chuti Resort Purbachal
              </span>
              <FaBuilding className="text-blue-400 text-xl" />
            </div>
            <h1 className="text-4xl md:text-6xl font-fancy drop-shadow-lg mb-2">
              Meeting / Conference
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto">
              Professional meeting and conference facilities at Chuti Resort Purbachal
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Amenities Strip ── */}
      <div className="bg-blue-900 py-4 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-5 md:gap-10">
          {amenities.map((a) => (
            <div key={a.label} className="flex items-center gap-2 text-white text-sm">
              <span className="text-blue-300">{a.icon}</span>
              <span>{a.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── About ── */}
      <section className="max-w-6xl mx-auto px-4 py-7">
        <div className="grid md:grid-cols-2 gap-8 items-start">

          {/* Left */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="text-blue-600 uppercase tracking-widest text-xs font-bold">About the Venue</span>
            <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2 mb-3">Meeting / Conference</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              A premier destination for corporate conferences, business meetings, seminars, workshops, and large-scale events — offering three dedicated meeting rooms and an open amphitheatre at Chuti Resort Purbachal.
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <FaClock className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Availability</p>
                  <p className="text-gray-500">7 Days a Week</p>
                  <p className="text-gray-500">8:00 AM – 10:00 PM</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <FaMapMarkerAlt className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Location</p>
                  <p className="text-gray-500">Convention Wing</p>
                  <p className="text-gray-500">Chuti Resort Purbachal</p>
                  <p className="text-gray-500">Purbachal, Dhaka</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <FaUsers className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Venues</p>
                  <p className="text-gray-500">Kanchan — up to 130+</p>
                  <p className="text-gray-500">Nagori — up to 72+</p>
                  <p className="text-gray-500">Rathura — up to 20+</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <FaPhoneAlt className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Booking</p>
                  <p className="text-gray-500">Custom packages available</p>
                  <p className="text-gray-500">Advance booking required</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            {/* Premium stat strip */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { value: "3+", label: "Venues", icon: "🏛️" },
                { value: "130+", label: "Max Capacity", icon: "👥" },
                { value: "24/7", label: "Support", icon: "🤝" },
              ].map((s) => (
                <div key={s.label} className="relative overflow-hidden bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-2xl p-4 text-center shadow-lg border border-blue-600/30">
                  <div className="absolute -top-2 -right-2 text-3xl opacity-10 select-none">{s.icon}</div>
                  <p className="text-2xl font-extrabold font-fancy tracking-tight">{s.value}</p>
                  <p className="text-blue-300 text-[11px] uppercase tracking-widest mt-0.5 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[img2, img3, img4, img5].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={galleryImages[i + 1].caption}
                  className={`rounded-2xl object-cover w-full h-44 shadow-md cursor-pointer hover:scale-105 transition-transform duration-300 ${i === 1 ? "mt-5" : i === 2 ? "-mt-5" : ""}`}
                  onClick={() => setLightboxIdx(i + 1)}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Meeting Facilities Table ── */}
      <section className="bg-gradient-to-br from-blue-950 to-blue-800 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-blue-300 uppercase tracking-widest text-xs font-bold">Specifications</span>
            <h2 className="text-3xl md:text-4xl font-fancy text-white mt-1">Meeting Facilities</h2>
            <p className="text-blue-300/70 mt-1 text-sm">Room sizes, seating capacities and banner dimensions</p>
          </motion.div>

          {/* Desktop table */}
          <motion.div
            className="hidden md:block overflow-hidden rounded-2xl shadow-2xl"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-900/80 text-blue-200 uppercase tracking-wider text-xs">
                  <th className="px-4 py-4 text-left w-8">#</th>
                  <th className="px-4 py-4 text-left">Name of Service</th>
                  <th className="px-4 py-4 text-left">Venue</th>
                  <th className="px-4 py-4 text-center">Rate</th>
                  <th className="px-3 py-4 text-center" colSpan={2}>
                    <span className="block mb-1">Room Size</span>
                    <div className="flex justify-center gap-4 text-[10px] normal-case font-normal text-blue-300">
                      <span>Length (ft)</span>
                      <span>Width (ft)</span>
                    </div>
                  </th>
                  <th className="px-2 py-4 text-center" colSpan={4}>
                    <span className="block mb-1">Seating Capacity</span>
                    <div className="flex justify-center gap-3 text-[10px] normal-case font-normal text-blue-300">
                      <span>Classroom</span>
                      <span>Theatre</span>
                      <span>U-Shape</span>
                      <span>Banquet</span>
                    </div>
                  </th>
                  <th className="px-4 py-4 text-center">Banner</th>
                </tr>
              </thead>
              <tbody>
                {facilityRows.map((row, i) => (
                  <tr key={row.sl} className={`border-t border-blue-700/40 transition-colors ${i % 2 === 0 ? "bg-blue-900/30" : "bg-blue-800/20"} hover:bg-blue-700/30`}>
                    <td className="px-4 py-4 text-blue-300 font-bold">{row.sl}</td>
                    <td className="px-4 py-4 text-white font-medium">{row.name}</td>
                    <td className="px-4 py-4">
                      <span className="bg-blue-500/30 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-400/40">
                        {row.venue}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-amber-300 font-bold">৳ {row.rate}</td>
                    <td className="px-3 py-4 text-center text-white font-semibold">{row.length}</td>
                    <td className="px-3 py-4 text-center text-white font-semibold">{row.width}</td>
                    <td className="px-2 py-4 text-center text-white">{row.classroom}</td>
                    <td className="px-2 py-4 text-center text-white font-bold">{row.theatre}</td>
                    <td className="px-2 py-4 text-center text-white">{row.ushape}</td>
                    <td className="px-2 py-4 text-center text-white">{row.banquet}</td>
                    <td className="px-4 py-4 text-center">
                      <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-2 py-1 rounded-lg border border-amber-500/30">
                        {row.banner}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Amphitheatre row */}
                <tr className="border-t border-blue-700/40 bg-blue-900/30 hover:bg-blue-700/30">
                  <td className="px-4 py-4 text-blue-300 font-bold">4</td>
                  <td className="px-4 py-5 text-white font-medium" colSpan={3}>
                    <p className="font-semibold">Amphitheatre</p>
                    <p className="text-blue-300 text-xs mt-1">Full Side Banner = 15×7 &nbsp;|&nbsp; Middle Side Banner = 8×7</p>
                  </td>
                  <td className="px-3 py-4 text-center text-blue-400/60" colSpan={6}>—</td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-col gap-1 items-center">
                      <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-2 py-1 rounded-lg border border-amber-500/30">15×7</span>
                      <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-2 py-1 rounded-lg border border-amber-500/30">8×7</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-4">
            {facilityRows.map((row, i) => (
              <motion.div
                key={row.sl}
                className="bg-blue-900/50 border border-blue-700/40 rounded-2xl p-5 backdrop-blur-sm"
                initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="bg-blue-500/30 text-blue-200 text-xs font-bold px-3 py-1 rounded-full border border-blue-400/40 mr-2">{row.venue}</span>
                    <p className="text-white font-semibold mt-2">{row.name}</p>
                  </div>
                  <span className="text-amber-300 font-bold text-sm whitespace-nowrap">৳ {row.rate}</span>
                </div>
                <p className="text-blue-300 text-xs mb-3">Room: {row.length} × {row.width} ft &nbsp;·&nbsp; Banner: {row.banner}</p>
                <div className="grid grid-cols-4 gap-2">
                  <CapBadge label="Classroom" value={row.classroom} color="bg-blue-800/60 text-white" />
                  <CapBadge label="Theatre" value={row.theatre} color="bg-blue-600/60 text-white" />
                  <CapBadge label="U-Shape" value={row.ushape} color="bg-blue-800/60 text-white" />
                  <CapBadge label="Banquet" value={row.banquet} color="bg-blue-800/60 text-white" />
                </div>
              </motion.div>
            ))}
            {/* Amphitheatre mobile card */}
            <motion.div
              className="bg-blue-900/50 border border-blue-700/40 rounded-2xl p-5 backdrop-blur-sm"
              initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.24 }}
            >
              <p className="text-white font-semibold mb-2">Amphitheatre</p>
              <div className="flex gap-2 flex-wrap">
                <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-500/30">Full Side Banner: 15×7</span>
                <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-500/30">Middle Side Banner: 8×7</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Venue Cards ── */}
      <section className="bg-gray-100 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-7" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-blue-600 uppercase tracking-widest text-xs font-bold">Our Venues</span>
            <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2">Conference Rooms</h2>
            <p className="text-gray-400 mt-2 text-sm max-w-md mx-auto">Three purpose-built venues to suit every type of event</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {venues.map((venue, i) => (
              <motion.div
                key={venue.name}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
                initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
              >
                {/* Card header */}
                <div className="bg-gradient-to-br from-blue-700 to-blue-950 p-6 text-white">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <p className="text-blue-300 text-xs font-semibold uppercase tracking-widest mb-1">{venue.subtitle}</p>
                      <h3 className="text-2xl font-fancy font-bold">{venue.name}</h3>
                    </div>
                    <span className="bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap">
                      ৳ {venue.rate}/-++
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 text-blue-200 text-xs">
                    <span className="bg-blue-600/40 px-2 py-0.5 rounded-full">📐 {venue.size}</span>
                    <span className="bg-blue-600/40 px-2 py-0.5 rounded-full">🪧 Banner {venue.banner}</span>
                  </div>
                  {/* Capacity chips */}
                  <div className="grid grid-cols-4 gap-1.5 mt-4">
                    {[
                      { l: "Class", v: venue.capacity.classroom },
                      { l: "Theatre", v: venue.capacity.theatre },
                      { l: "U-Shape", v: venue.capacity.ushape },
                      { l: "Banquet", v: venue.capacity.banquet },
                    ].map((c) => (
                      <div key={c.l} className="bg-white/10 rounded-lg p-1.5 text-center">
                        <p className="text-white font-bold text-base font-fancy leading-none">{c.v}</p>
                        <p className="text-blue-300 text-[9px] uppercase tracking-wide mt-0.5">{c.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{venue.desc}</p>
                  <ul className="space-y-2">
                    {venue.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <FaCheckCircle className="text-blue-400 flex-shrink-0 text-xs" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Amphitheatre card */}
          <motion.div
            className="mt-6 bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 md:p-8 text-white shadow-xl"
            initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-1">Outdoor Venue</p>
                <h3 className="text-2xl md:text-3xl font-fancy font-bold">Amphitheatre</h3>
                <p className="text-blue-200 text-sm mt-2 max-w-xl">
                  Our open-air amphitheatre is perfect for large outdoor events, cultural programmes, product launches, and evening galas — with ample banner space for branding and sponsorship displays.
                </p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-3 text-center">
                  <p className="text-amber-300 font-bold text-lg font-fancy">15×7</p>
                  <p className="text-blue-300 text-xs mt-0.5">Full Side Banner</p>
                </div>
                <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-3 text-center">
                  <p className="text-amber-300 font-bold text-lg font-fancy">8×7</p>
                  <p className="text-blue-300 text-xs mt-0.5">Middle Side Banner</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Photo Gallery ── */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <motion.div className="text-center mb-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="text-blue-600 uppercase tracking-widest text-xs font-bold">Gallery</span>
          <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2">Our Spaces in Action</h2>
          <p className="text-gray-400 mt-2 text-sm">Click any image to view full screen</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <motion.div
            className="col-span-2 row-span-2 overflow-hidden rounded-2xl shadow-lg cursor-pointer group"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            onClick={() => setLightboxIdx(0)}
          >
            <div className="relative h-full">
              <img src={img1} alt="Main Hall" className="w-full h-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                <span className="text-white font-medium font-fancy text-lg">Main Conference Hall</span>
              </div>
            </div>
          </motion.div>

          {galleryImages.slice(1).map((img, i) => (
            <motion.div
              key={i + 1}
              className="overflow-hidden rounded-2xl shadow-md cursor-pointer group"
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              onClick={() => setLightboxIdx(i + 1)}
            >
              <div className="relative overflow-hidden">
                <img src={img.src} alt={img.caption} className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-300 flex items-end">
                  <p className="text-white text-xs font-medium p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">{img.caption}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 py-8 px-4 text-white text-center">
        <motion.div className="max-w-2xl mx-auto" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h3 className="text-3xl font-fancy mb-2">Plan Your Next Event With Us</h3>
          <p className="text-blue-200 mb-5">
            Contact our dedicated events team to discuss your requirements. We will create a tailored package to make your event a resounding success.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white hover:bg-blue-50 text-blue-900 font-semibold px-8 py-3 rounded-full transition-colors duration-300 shadow-lg"
          >
            Get a Quote
          </a>
        </motion.div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxIdx !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % galleryImages.length)}
          onJump={(idx) => setLightboxIdx(idx)}
        />
      )}
    </div>
  );
}

export default Conference;
